import {
  CONTENT_TYPES,
  VOICE_CALL_STATUS,
} from 'dashboard/components-next/message/constants';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { useCallsStore } from 'dashboard/stores/calls';
import types from 'dashboard/store/mutation-types';

export const TERMINAL_STATUSES = [
  'completed',
  'busy',
  'failed',
  'rejected',
  'no-answer',
  'canceled',
  'missed',
  'ended',
];

// Design 21: warn the agent at two minutes on hold.
export const HOLD_WARNING_SECONDS = 120;

export const TERMINAL_TRANSFER_STATUSES = [
  'completed',
  'rejected',
  'timed_out',
  'cancelled',
  'failed',
  'returned',
];

export const isInbound = direction => direction === 'inbound';

const isVoiceCallMessage = message => {
  return CONTENT_TYPES.VOICE_CALL === message?.content_type;
};

const shouldSkipCall = (callDirection, senderId, currentUserId) => {
  return callDirection === 'outbound' && senderId !== currentUserId;
};

const extractAssigneeId = conversation => {
  return conversation?.assignee_id || conversation?.meta?.assignee?.id || null;
};

const isAssignedToAnotherAgent = (assigneeId, currentUserId) => {
  if (currentUserId == null) return false;
  return !!assigneeId && assigneeId !== currentUserId;
};

// ringing_agent_ids (Voice::RingTargetsService) is the server's decision on who
// rings: every free agent of the inbox, else every online one (owner decision,
// 28 Sep 2026: ring everyone available). When it is present it replaces the
// assignee rule, because a returning caller's call reuses a conversation still
// assigned to whoever had it last - hiding the call from everyone but an
// offline assignee meant nobody rang (28 Sep 2026). Its absence (older
// messages, providers that do not stamp it) keeps the assignee rule.
const shouldShowCall = ({
  callDirection,
  senderId,
  assigneeId,
  currentUserId,
  ringingAgentIds,
}) => {
  if (shouldSkipCall(callDirection, senderId, currentUserId)) return false;
  // Outbound calls are scoped to the initiator via shouldSkipCall; the
  // conversation may be auto-assigned to a different agent on creation, so
  // skip the assignee filter for outbound to avoid hiding the caller's own widget.
  if (callDirection === 'outbound') return true;
  if (Array.isArray(ringingAgentIds)) {
    return ringingAgentIds.includes(currentUserId);
  }
  return !isAssignedToAnotherAgent(assigneeId, currentUserId);
};

// Every available agent rings (owner decision, 28 Sep 2026), so while
// colleagues are still being rung one agent's Decline only silences their own
// card instead of hanging up on the caller.
export const othersStillRinging = (call, currentUserId) =>
  call?.callDirection === 'inbound' &&
  Array.isArray(call?.ringingAgentIds) &&
  call.ringingAgentIds.some(id => id !== currentUserId);

// Offline/busy agents shouldn't get a ringing popup for inbound calls, but
// outbound calls always belong to the initiator regardless of their status,
// and existing (already-surfaced) calls keep going so a status change
// mid-call doesn't yank away an active widget.
const shouldRingInbound = (callDirection, currentUserAvailability) => {
  if (callDirection === 'outbound') return true;
  return currentUserAvailability === 'online';
};

function extractCallerSnapshot(message) {
  // Snapshot caller info from the message at add-time so the widget can keep
  // rendering it after the user navigates away from a conversation list that
  // had the conversation hydrated (and Vuex evicts it from the store).
  // Only incoming messages carry the contact as the sender; on outbound calls
  // the sender is the initiating agent, so skip the snapshot and let the widget
  // fall back to the conversation's contact (conversation.meta.sender).
  if (message?.message_type !== MESSAGE_TYPE.INCOMING) return null;
  const sender = message?.sender;
  if (!sender) return null;
  return {
    name: sender.name,
    phone: sender.phone_number,
    avatar: sender.avatar || sender.thumbnail,
    additionalAttributes: sender.additional_attributes || {},
  };
}

function extractCallData(message) {
  const call = message?.call || {};
  return {
    callSid: call.provider_call_id,
    callId: call.id,
    provider: call.provider,
    status: call.status,
    callDirection: call.direction === 'outgoing' ? 'outbound' : 'inbound',
    conversationId: message?.conversation_id,
    inboxId: message?.inbox_id ?? message?.conversation?.inbox_id,
    assigneeId: extractAssigneeId(message?.conversation),
    senderId: message?.sender?.id,
    caller: extractCallerSnapshot(message),
    // Call#push_event_data (enterprise/app/models/call.rb) merges these into
    // every voice_call message payload, so a reload that rehydrates the call
    // list from message data (design 14.4) must reseed hold state from here
    // too, not just from the HTTP hold/resume response and the cable broadcast.
    onHold: call.on_hold,
    holdSeconds: call.hold_seconds,
    // Same reseed, for the originator's pending transfer (design 14.4). A
    // terminal active_transfer (e.g. the tail end of a just-completed
    // transfer landing in the same payload) is not a pending offer, so it
    // is dropped here rather than reseeded as one.
    transfer:
      call.active_transfer &&
      !TERMINAL_TRANSFER_STATUSES.includes(call.active_transfer.status)
        ? call.active_transfer
        : null,
    // Call#push_event_data carries the two-agent roster whenever
    // pbx_call_participants is on. Carried here for parity with `transfer`;
    // note that the only addCall path a message payload reaches is the
    // `ringing` one, where there is never a second agent — the live card is
    // fed by the two cable events, not by this (spec R12).
    connectedAgents: call.connected_agents,
    // Voice::CallMessageBuilder stamps this on the voice_call message so the
    // message path agrees with the ring cable path on who should ring.
    ringingAgentIds: message?.content_attributes?.data?.ringing_agent_ids,
  };
}

export function handleVoiceCallCreated(
  message,
  currentUserId,
  currentUserAvailability
) {
  if (!isVoiceCallMessage(message)) return;

  const {
    callSid,
    callId,
    provider,
    status,
    callDirection,
    conversationId,
    inboxId,
    assigneeId,
    senderId,
    onHold,
    holdSeconds,
    transfer,
    connectedAgents,
    ringingAgentIds,
  } = extractCallData(message);

  // A voice_call message can be created already terminal when the caller hangs
  // up before connect. Only ring while the call is actually ringing; mirrors the
  // guard in seedCallsFromHydratedMessages.
  if (status !== VOICE_CALL_STATUS.RINGING) return;

  if (
    !shouldShowCall({
      callDirection,
      senderId,
      assigneeId,
      currentUserId,
      ringingAgentIds,
    })
  ) {
    return;
  }

  if (!shouldRingInbound(callDirection, currentUserAvailability)) return;

  const callsStore = useCallsStore();
  callsStore.addCall({
    callSid,
    callId,
    provider,
    onHold,
    holdSeconds,
    transfer,
    connectedAgents,
    conversationId,
    inboxId,
    callDirection,
    senderId,
    ringingAgentIds,
    caller: extractCallerSnapshot(message),
  });
}

export function handleVoiceCallUpdated(
  commit,
  message,
  currentUserId,
  currentUserAvailability
) {
  if (!isVoiceCallMessage(message)) return;

  const {
    callSid,
    callId,
    provider,
    status,
    callDirection,
    conversationId,
    inboxId,
    assigneeId,
    senderId,
    onHold,
    holdSeconds,
    transfer,
    connectedAgents,
    ringingAgentIds,
  } = extractCallData(message);

  const callsStore = useCallsStore();

  callsStore.handleCallStatusChanged({ callSid, status, conversationId });

  commit(types.UPDATE_MESSAGE_CALL_STATUS, {
    conversationId,
    callStatus: status,
    callSid,
  });

  if (
    !shouldShowCall({
      callDirection,
      senderId,
      assigneeId,
      currentUserId,
      ringingAgentIds,
    })
  ) {
    callsStore.removeCall(callSid);
    return;
  }

  // Another agent answered. The voice_call.accepted cable event only reaches
  // the agents the call was routed to, but this message update reaches every
  // agent who could be ringing (inbox members and administrators), so it is
  // the reliable place to stop the ring. Never touch an active call.
  // The payload carries Call#display_status ('in-progress'), and Twilio names
  // the answering agent only as call.accepted_by_agent_id - comparing against
  // 'in_progress' meant this never fired (28 Sep 2026).
  if (status === VOICE_CALL_STATUS.IN_PROGRESS) {
    const acceptedById =
      message?.call?.accepted_by_agent_id ??
      message?.content_attributes?.data?.accepted_by?.id;
    const ringingEntry = callsStore.calls.find(c => c.callSid === callSid);
    if (
      ringingEntry &&
      !ringingEntry.isActive &&
      acceptedById &&
      acceptedById !== currentUserId
    ) {
      callsStore.dismissCall(callSid);
    }
    return;
  }

  if (status === VOICE_CALL_STATUS.RINGING) {
    if (!shouldRingInbound(callDirection, currentUserAvailability)) return;

    callsStore.addCall({
      callSid,
      callId,
      provider,
      onHold,
      holdSeconds,
      transfer,
      connectedAgents,
      conversationId,
      inboxId,
      callDirection,
      senderId,
      ringingAgentIds,
      caller: extractCallerSnapshot(message),
    });
  }
}

export function syncConversationCallVisibility(conversation, currentUserId) {
  const assigneeId = extractAssigneeId(conversation);
  if (!isAssignedToAnotherAgent(assigneeId, currentUserId)) return;

  // Outbound calls belong to the initiator regardless of who the conversation
  // is currently assigned to (auto-assignment may flip mid-call). Mirror
  // shouldShowCall's outbound exception so an in-progress outbound call isn't
  // ripped out from under the caller when the conversation reassigns.
  const callsStore = useCallsStore();
  const callsToRemove = callsStore.calls.filter(
    call =>
      call.conversationId === conversation.id &&
      !shouldShowCall({
        callDirection: call.callDirection,
        senderId: call.senderId,
        assigneeId,
        currentUserId,
        ringingAgentIds: call.ringingAgentIds,
      })
  );
  callsToRemove.forEach(call => callsStore.removeCall(call.callSid));
}
